import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Avatar, Box, CircularProgress, Grid, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import { getCityAnalytics } from "../../api/analyticsService";
import { PeriodSelector, TopProductsCard, CategoryPerformanceCard, LocationTopStoresCard } from "./analyticsShared";

export default function CityAnalyticsDetail() {
  const { cityId } = useParams();
  const navigate = useNavigate();
  const [period, setPeriod] = useState("30d");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (period === "custom" && (!customStart || !customEnd)) return;

    let active = true;
    setLoading(true);
    getCityAnalytics({
      cityId,
      period,
      startDate: period === "custom" ? customStart : undefined,
      endDate: period === "custom" ? customEnd : undefined,
    })
      .then((result) => {
        if (active) {
          setData(result);
          setError("");
        }
      })
      .catch(() => {
        if (active) setError("Could not load analytics. Is the API running?");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [cityId, period, customStart, customEnd]);

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        sx={{ mb: 3 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title="Back to Analytics">
            <IconButton onClick={() => navigate("/admin/analytics")} sx={{ border: "1px solid", borderColor: "divider" }}>
              <ArrowBackRoundedIcon />
            </IconButton>
          </Tooltip>
          <Avatar sx={{ width: 44, height: 44, bgcolor: "primary.main" }}>
            <LocationOnRoundedIcon />
          </Avatar>
          <Box>
            <Typography variant="h4">{data?.cityNameEnglish || data?.city || "City analytics"}</Typography>
            <Typography variant="body2" color="text.secondary">
              {data?.districtName || "Top products among buyers based in this city."}
            </Typography>
          </Box>
        </Stack>
        <PeriodSelector
          period={period}
          onPeriodChange={setPeriod}
          customStart={customStart}
          customEnd={customEnd}
          onCustomChange={(start, end) => {
            setCustomStart(start);
            setCustomEnd(end);
          }}
        />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && !data ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : data ? (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <CategoryPerformanceCard data={data.categoryPerformance} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <LocationTopStoresCard
                data={data.topStores}
                subtitle="Most popular stores among buyers based in this city, this period"
                onStoreClick={(s) => navigate(`/admin/analytics/stores/${s.storeId}`)}
              />
            </Grid>
          </Grid>
          <TopProductsCard data={data.topProducts} subtitle="Top 20 by metric, among buyers based in this city, this period" />
        </>
      ) : null}
    </Box>
  );
}
